import ExclamationTriangleIcon from '@heroicons/react/24/outline/ExclamationTriangleIcon';
import { ComponentPropsWithoutRef, ComponentType } from 'react';

import { useFormatAttributeValue } from '@/components/datamodel/utils/formatAttributeValue';
import { useTranslation } from '@/hooks/useTranslation';
import type { AssetApiRecord } from '@/pages/dam/api/assets.types';
import type { DataModelAttribute } from '@/types/DataModel';

const COMPLIANT = 'compliant__';

interface DamStatusBadge {
    label: string;
    variant: 'neutral' | 'warning';
    icon?: ComponentType<ComponentPropsWithoutRef<'svg'>>;
}

const hasValue = (value: unknown): boolean =>
    value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0);

/**
 * Status badges of a badge section (`asset_card` `BOTTOM_BADGES`, `asset_detail`
 * `HEADER_BADGES`): one neutral badge per value; `compliant__` only as a leading warning
 * badge "Non-compliant" and nothing while the asset is compliant (D-051).
 */
function useDamStatusBadges() {
    const { t } = useTranslation();
    const { formatValue } = useFormatAttributeValue();

    return (asset: AssetApiRecord, attributes: DataModelAttribute[]): DamStatusBadge[] => {
        const isNonCompliant = attributes.some((attribute) => attribute.name === COMPLIANT) && asset[COMPLIANT] === false;
        const values = attributes.filter((attribute) => attribute.name !== COMPLIANT).flatMap((attribute) => {
            const value = asset[attribute.name];
            if (!hasValue(value)) {
                return [];
            }
            return (Array.isArray(value) ? value : [value]).map((single): DamStatusBadge => ({
                label: attribute.name === 'filename__' ? String(single) : formatValue(attribute, single),
                variant: 'neutral',
            }));
        });
        return isNonCompliant
            ? [{ label: t('DAM.CARD.NON_COMPLIANT'), variant: 'warning', icon: ExclamationTriangleIcon }, ...values]
            : values;
    };
}

export { useDamStatusBadges };
export type { DamStatusBadge };
